import { apiClient } from '@borradh-workspace/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { CreateLeadFormInput, LeadForm } from '../types';

export const useCreateLeadForm = (options?: {
  onSuccess?: (leadForm: LeadForm) => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: CreateLeadFormInput) => {
      return apiClient.post<LeadForm>('lead-forms', input);
    },
    onSuccess: (leadForm) => {
      void queryClient.invalidateQueries({ queryKey: ['lead-forms'] });
      if (leadForm.status === 'error') {
        toast.error('Form saved, but publishing failed', {
          description: leadForm.syncError || 'Save again to retry.',
        });
        return;
      }
      toast.success(
        leadForm.status === 'synced' ? 'Lead form published' : 'Lead form saved'
      );
      options?.onSuccess?.(leadForm);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create lead form');
      options?.onError?.(error);
    },
  });

  return {
    leadForm: mutation.data,
    reset: mutation.reset,
    createLeadForm: mutation.mutate,
    createLeadFormAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
  };
};
